import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchJson } from '@/lib/api';
import { INPUT, Modal, Row, subirFoto } from './comunes';

/** Abrir un reclamo sobre una cobertura, con la foto del dano si la hay. */
export default function ModalReclamo({ poliza, token, onClose, onFiled }) {
  const [tipo, setTipo] = useState('');
  const [fecha, setFecha] = useState('');
  const [descubierto, setDescubierto] = useState('');
  const [monto, setMonto] = useState('');
  const [desc, setDesc] = useState('');
  const [foto, setFoto] = useState(null);
  const [serial, setSerial] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const enviar = async () => {
    setBusy(true); setError('');
    try {
      const d = await fetchJson('/insurance/selletrans/claims', {
        token, method: 'POST',
        body: {
          verification_number: poliza.verification_number,
          claim_type: tipo,
          incident_date: fecha,
          discovered_date: descubierto || undefined,
          description: desc,
          amount_claimed: parseFloat(monto),
        },
      });
      let extra = '';
      if (foto) {
        // El reclamo ya existe. Si la foto no sube, se dice: no se puede
        // dejar creer que quedo adjunta.
        try {
          await subirFoto(`/photo-evidence/claim/${d.claim_number}`, token, foto, serial);
          extra = ' · photo attached';
        } catch (pe) { extra = ` · the photo did NOT attach: ${pe.message}`; }
      }
      const papeles = d.records_due_at ? ` · send the supporting records by ${new Date(d.records_due_at).toLocaleDateString()}` : '';
      const tarde = d.late_notice ? ` · reported ${d.notice_days} days after discovery: it will be reviewed, not denied for timing alone` : '';
      onFiled(`Claim ${d.claim_number} filed · payable estimate $${Number(d.payable_estimate).toFixed(2)}${papeles}${tarde}${extra}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title={`File claim on ${poliza.verification_number}`} testid="claim-modal">
      <div className="space-y-3">
        <p className="text-xs text-slate-500">Covers: <b>{poliza.covers?.join(', ')}</b> · Max payout ${poliza.max_payout?.toLocaleString()} · Deductible ${(poliza.deductible || 0).toLocaleString()}</p>
        <Row label="Claim type">
          <select className={INPUT} value={tipo} onChange={e => setTipo(e.target.value)} data-testid="cl-type">
            <option value="">Select type…</option>
            {(poliza.covers || []).map(c => <option key={c} value={c}>{c.replaceAll('_', ' ')}</option>)}
          </select>
        </Row>
        <Row label="Incident date">
          <input type="date" className={INPUT} value={fecha} onChange={e => setFecha(e.target.value)} data-testid="cl-date" />
        </Row>
        <Row label="Date you discovered it (if later)">
          <input type="date" className={INPUT} value={descubierto} onChange={e => setDescubierto(e.target.value)} data-testid="cl-found" />
        </Row>
        <p className="text-[11px] text-slate-500" data-testid="cl-deadlines">
          Report within 48 hours of discovering the problem and send the supporting
          records within 14 days. A late report is reviewed, not denied for timing alone.
        </p>
        <Row label="Amount claimed ($)">
          <input type="number" min={1} className={INPUT} value={monto} onChange={e => setMonto(e.target.value)} data-testid="cl-amount" />
        </Row>
        <Row label="What happened?">
          <textarea rows={3} className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" value={desc} onChange={e => setDesc(e.target.value)} data-testid="cl-desc" />
        </Row>

        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid="cl-photo-section">
          <p className="text-xs font-semibold">Photo of the damage (optional)</p>
          <input type="file" accept="image/jpeg,image/png,image/webp"
            onChange={e => setFoto(e.target.files?.[0] || null)}
            className="w-full text-xs" data-testid="cl-photo" />
          <input placeholder="Serial, if the item has one (optional)"
            value={serial} onChange={e => setSerial(e.target.value)}
            className="w-full h-9 px-2 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-xs font-mono"
            data-testid="cl-photo-serial" />
          <p className="text-[11px] text-slate-500">
            Stored as uploaded, with its hash and the time it arrived. It is
            compared with the item photo by a person, not by the system.
          </p>
        </div>

        <Button onClick={enviar} disabled={busy || !tipo || !fecha || !monto || !desc} className="w-full bg-gradient-to-r from-purple-500 to-indigo-500" data-testid="cl-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit claim'}
        </Button>
        {error && (
          <div className="p-2.5 rounded-lg bg-rose-50 text-rose-700 text-xs flex items-start gap-2" data-testid="cl-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}
